import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { X } from 'lucide-react'
import { useCreateLead } from '../../api/leads'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card'

const contactMethodOptions = [
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Телефон' },
  { value: 'telegram', label: 'Telegram' },
  { value: 'whatsapp', label: 'WhatsApp' },
] as const

const formSchema = z.object({
  name: z.string().min(1, 'Имя обязательно'),
  email: z.string().email('Некорректный email').optional().or(z.literal('')),
  phone: z.string().optional(),
  telegram: z.string().optional(),
  message: z.string().optional(),
  source: z.enum(['manual', 'website', 'telegram', 'referral']).default('manual'),
  preferredContact: z.array(z.string()).optional(),
})

type FormData = z.infer<typeof formSchema>

interface LeadFormProps {
  onClose: () => void
  onSuccess: () => void
}

export default function LeadForm({ onClose, onSuccess }: LeadFormProps) {
  const createLead = useCreateLead()

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
  })

  const onSubmit = async (data: FormData) => {
    try {
      await createLead.mutateAsync({
        name: data.name,
        email: data.email || undefined,
        phone: data.phone || undefined,
        telegram: data.telegram || undefined,
        message: data.message || undefined,
        source: data.source,
        preferredContact: data.preferredContact?.length ? data.preferredContact : undefined,
      })
      onSuccess()
    } catch (error) {
      console.error('Failed to create lead:', error)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <Card className="relative w-full max-w-lg mx-4 border-border-medium z-10">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Новая заявка</CardTitle>
          <button
            onClick={onClose}
            className="p-1 rounded-md text-text-tertiary hover:text-text-primary hover:bg-bg-tertiary transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <Input
              {...register('name')}
              label="Имя *"
              placeholder="Иван Петров"
              error={errors.name?.message}
            />

            <div className="grid grid-cols-2 gap-4">
              <Input
                {...register('email')}
                type="email"
                label="Email"
                placeholder="email@example.com"
                error={errors.email?.message}
              />
              <Input
                {...register('phone')}
                label="Телефон"
                placeholder="+7 999 123-45-67"
              />
            </div>

            <Input
              {...register('telegram')}
              label="Telegram"
              placeholder="@username"
            />

            <div>
              <label className="block text-sm font-medium text-text-secondary mb-2">
                Источник
              </label>
              <select
                {...register('source')}
                className="w-full px-4 py-3 bg-bg-tertiary border border-border-medium rounded-md text-text-primary focus:outline-none focus:border-accent-lime focus:ring-1 focus:ring-accent-lime/20 transition-all duration-200"
              >
                <option value="manual">Вручную</option>
                <option value="website">Сайт</option>
                <option value="telegram">Telegram</option>
                <option value="referral">Реферал</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-secondary mb-2">
                Предпочтительный способ связи
              </label>
              <div className="flex flex-wrap gap-3">
                {contactMethodOptions.map(({ value, label }) => (
                  <label key={value} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      value={value}
                      {...register('preferredContact')}
                      className="w-4 h-4 rounded border-border-medium bg-bg-tertiary text-accent-lime focus:ring-accent-lime/20"
                    />
                    <span className="text-sm text-text-primary">{label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-secondary mb-2">
                Сообщение
              </label>
              <textarea
                {...register('message')}
                rows={3}
                className="w-full px-4 py-3 bg-bg-tertiary border border-border-medium rounded-md text-text-primary placeholder:text-text-tertiary focus:outline-none focus:border-accent-lime focus:ring-1 focus:ring-accent-lime/20 transition-all duration-200 resize-none"
                placeholder="Описание запроса..."
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="secondary" onClick={onClose}>
                Отмена
              </Button>
              <Button type="submit" loading={createLead.isPending}>
                Создать
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
